import { Component, OnInit, OnDestroy, HostListener, inject } from '@angular/core';
import { RouteReplayDataService } from '../../services/route-replay-data.service';
import { RouteReplayMapControlService, VehiclePosition } from '../../services/route-replay-map-control.service';
import { Vehicle } from '../../models/route-replay-response.model';
import { Subscription } from 'rxjs';
import { ToastService } from 'mis-crystal-design-system/toast';
import { IMG_CONSTANTS } from '../../constants/img-constants';

interface TripMarker {
  type: 'TRIP_START' | 'TRIP_END';
  time: number;
  marginLeft: number;
  color: string;
  vehicleId: string;
  registration: string;
}

interface EmployeeMarker {
  type: 'SIGN_IN' | 'SIGN_OUT';
  time: number;
  marginLeft: number;
  color: string;
  vehicleId: string;
  registration: string;
  employeeCount: number;
  direction: string; // 'Login' or 'Logout'
  showCount?: boolean; // For hover state
}

@Component({
  selector: 'app-route-route-player',
  templateUrl: './route-route-player.component.html',
  styleUrl: './route-route-player.component.scss'
})
export class RouteRoutePlayerComponent implements OnInit, OnDestroy {
  vehicles: Vehicle[] = [];
  tripMarkers: TripMarker[] = [];
  employeeMarkers: EmployeeMarker[] = [];
  showEmployeeMarkers: boolean = false;
  
  globalTimeRange = { min: 0, max: 0, duration: 0 };
  currentTime: number = 0;
  isPaused: boolean = true;
  playbackSpeed: number = 1;
  progress: number = 0;
  zoom: number = 100;
  showDropdown: boolean = false;
  isMouseDown: boolean = false;
  wasPlayingBeforeDrag: boolean = false;
  
  timestamps: string[] = [];
  currentTimeDisplay: string = '00:00';
  readonly imgConstants=IMG_CONSTANTS
  
  private playbackInterval: any;
  private subscriptions: Subscription[] = [];

  constructor(
    private routeReplayDataService: RouteReplayDataService,
    private mapControlService: RouteReplayMapControlService
  ) {
    this.toast = inject(ToastService);
  }

  private toast: ToastService;

  // Keyboard shortcuts
  @HostListener('document:keyup', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    switch (event.key) {
      case ' ':
        event.preventDefault();
        this.togglePlayPause();
        break;
      case 'ArrowLeft':
        this.skipBackward();
        break;
      case 'ArrowRight':
        this.skipForward();
        break;
    }
  }

  ngOnInit(): void {
    const routeDataSub = this.routeReplayDataService.multiRouteData$.subscribe(routes => {
      if (routes.length > 0) {
        this.tripMarkers = [];
        this.employeeMarkers = [];
        this.showEmployeeMarkers = false;
      }
    });
    this.subscriptions.push(routeDataSub);

    // Subscribe to selected vehicles
    const vehiclesSub = this.routeReplayDataService.selectedVehicles$.subscribe(vehicles => {
      // Mark that we've received a vehicle selection event (even if filtered to 0)
      this.hasReceivedVehicles = true;
      
      const previousVehicleCount = this.vehicles.length;
      const wasPlaying = !this.isPaused;
      
      // Always pause playback when vehicles change to prevent multiple intervals
      if (previousVehicleCount > 0) {
        this.pausePlayback();
      }
      
      this.vehicles = vehicles;
      
      if (vehicles.length > 0) {
        this.calculateGlobalTimeRange();
        
        this.buildTripMarkers();
        this.buildEmployeeMarkers();
        this.generateTimestamps();
        
        // Only reset currentTime if this is the first vehicle selection
        // Otherwise, maintain the current playback position
        if (previousVehicleCount === 0) {
          this.currentTime = this.globalTimeRange.min;
        } else {
          // Keep current time, but clamp it to the new time range
          this.currentTime = Math.max(
            this.globalTimeRange.min,
            Math.min(this.currentTime, this.globalTimeRange.max)
          );
        }
        
        this.updateProgress();
        this.updateVehiclePositions();
        
        // Resume playback if it was playing before
        if (wasPlaying && previousVehicleCount > 0) {
          this.startPlayback();
        }
      } else {
        // No vehicles selected, reset player state completely
        this.pausePlayback();
        this.resetPlayer();
      }
    });
    this.subscriptions.push(vehiclesSub);
  }

  calculateGlobalTimeRange(): void {
    if (this.vehicles.length === 0) return;

    const allStartTimes = this.vehicles
      .filter(v => v.milestonesData && v.milestonesData.locationList.length > 0)
      .map(v => v.milestonesData.locationList[0].time);
    
    const allEndTimes = this.vehicles
      .filter(v => v.milestonesData && v.milestonesData.locationList.length > 0)
      .map(v => v.milestonesData.locationList[v.milestonesData.locationList.length - 1].time);


    if (allStartTimes.length === 0 || allEndTimes.length === 0) {
      // No valid datetime data - don't show toast here, only when play is clicked
      return;
    }

    this.globalTimeRange = {
      min: Math.min(...allStartTimes),
      max: Math.max(...allEndTimes),
      duration: Math.max(...allEndTimes) - Math.min(...allStartTimes)
    };
  }

  buildTripMarkers(): void {
    this.tripMarkers = [];
    this.vehicles
      .filter(v => v.startDateTimeEpoch && v.endDateTimeEpoch)
      .forEach(vehicle => {
        const startTime = vehicle.startDateTimeEpoch;
        const endTime = vehicle.endDateTimeEpoch;

        // Add trip start marker
        this.tripMarkers.push({
          type: 'TRIP_START',
          time: startTime,
          marginLeft: this.calculateMarginLeft(startTime),
          color: vehicle.color || '#638FE7',
          vehicleId: vehicle.etsVehicle.guId,
          registration: vehicle.etsVehicle.registration
        });

        // Add trip end marker - use actual endDateTimeEpoch
        // The global time range already accounts for this
        this.tripMarkers.push({
          type: 'TRIP_END',
          time: endTime,
          marginLeft: this.calculateMarginLeft(endTime),
          color: vehicle.color || '#638FE7',
          vehicleId: vehicle.etsVehicle.guId,
          registration: vehicle.etsVehicle.registration
        });
      });

    // Sort markers by time
    this.tripMarkers.sort((a, b) => a.time - b.time);
  }

  buildEmployeeMarkers(): void {
    this.employeeMarkers = [];
    
    // Get route direction from data service
    const routeDirection = this.routeReplayDataService.getRouteDirection();
    
    this.vehicles
      .filter(v => v.employeeSignInData && v.employeeSignInData.length > 0)
      .forEach(vehicle => {
        // Group employees by timestamp
        const timestampGroups = new Map<number, string[]>();
        
        vehicle.employeeSignInData!.forEach(signIn => {
          if (!timestampGroups.has(signIn.timestamp)) {
            timestampGroups.set(signIn.timestamp, []);
          }
          timestampGroups.get(signIn.timestamp)!.push(signIn.empId);
        });
        
        // Create markers for each timestamp group
        timestampGroups.forEach((empIds, timestamp) => {
          this.employeeMarkers.push({
            type: routeDirection === 'Login' ? 'SIGN_IN' : 'SIGN_OUT',
            time: timestamp,
            marginLeft: this.calculateMarginLeft(timestamp),
            color: vehicle.color || '#638FE7',
            vehicleId: vehicle.etsVehicle.guId,
            registration: vehicle.etsVehicle.registration,
            employeeCount: empIds.length,
            direction: routeDirection
          });
        });
      });
    
    // Sort markers by time
    this.employeeMarkers.sort((a, b) => a.time - b.time);
  }

  toggleEmployeeMarkers($event: any): void {
    $event.stopPropagation();
    this.showEmployeeMarkers = !this.showEmployeeMarkers;
  }

  onEmployeeMarkerHover(marker: EmployeeMarker, isHovering: boolean): void {
    marker.showCount = isHovering;
  }

  calculateMarginLeft(time: number): number {
    if (this.globalTimeRange.duration === 0) return 0;
    return ((time - this.globalTimeRange.min) / this.globalTimeRange.duration) * 100;
  }

  calculateWidth(startTime: number, endTime: number): number {
    if (this.globalTimeRange.duration === 0) return 0;
    return ((endTime - startTime) / this.globalTimeRange.duration) * 100;
  }

  generateTimestamps(): void {
    this.timestamps = [];
    const interval = this.globalTimeRange.duration / 10;
    
    for (let i = 0; i <= 10; i++) {
      const time = this.globalTimeRange.min + (interval * i);
      this.timestamps.push(this.formatTime(time));
    }
  }

  formatTime(timestamp: number): string {
    const date = new Date(timestamp);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  togglePlayPause(): void {
    if (this.isPaused) {
      this.startPlayback();
    } else {
      this.pausePlayback();
    }
  }

  startPlayback(): void {
    // Check if there's valid data to play
    if (this.globalTimeRange.duration === 0 || this.globalTimeRange.max === 0) {
      this.toast.displayMsg('No location data available for playback');
      return;
    }
    
    if (this.currentTime >= this.globalTimeRange.max) {
      this.currentTime = this.globalTimeRange.min;
    }

    this.isPaused = false;
    this.mapControlService.updatePlaybackState({ isPlaying: true, speed: this.playbackSpeed });

    this.playbackInterval = setInterval(() => {
      // Interval runs every 100ms
      // At 1x: advance 100ms per tick (1000ms per second)
      // At 2x: advance 200ms per tick (2000ms per second)
      // At 4x: advance 400ms per tick (4000ms per second)
      const increment = 6000 * this.playbackSpeed;
      this.currentTime += increment;

      if (this.currentTime >= this.globalTimeRange.max) {
        this.currentTime = this.globalTimeRange.max;
        this.pausePlayback();
      }

      this.updateProgress();
      this.updateVehiclePositions();
    }, 100);
  }

  pausePlayback(): void {
    this.isPaused = true;
    this.mapControlService.updatePlaybackState({ isPlaying: false });
    
    if (this.playbackInterval) {
      clearInterval(this.playbackInterval);
      this.playbackInterval = null;
    }
  }

  skipBackward(): void {
    const wasPlaying = !this.isPaused;
    this.pausePlayback();
    
    this.currentTime = Math.max(this.globalTimeRange.min, this.currentTime - (5 * 60 * 1000));
    this.updateProgress();
    this.updateVehiclePositions();
    
    if (wasPlaying) {
      this.startPlayback();
    }
  }

  skipForward(): void {
    const wasPlaying = !this.isPaused;
    this.pausePlayback();
    
    this.currentTime = Math.min(this.globalTimeRange.max, this.currentTime + (5 * 60 * 1000));
    this.updateProgress();
    this.updateVehiclePositions();
    
    if (wasPlaying) {
      this.startPlayback();
    }
  }

  replay(): void {
    this.pausePlayback();
    this.currentTime = this.globalTimeRange.min;
    this.updateProgress();
    this.updateVehiclePositions();
    this.startPlayback();
  }

  onTimelineChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.currentTime = parseInt(target.value);
    this.updateProgress();
    this.updateVehiclePositions();
  }

  setPlaybackSpeed(speed: number): void {
    this.playbackSpeed = speed;
    this.showDropdown = false;
    this.mapControlService.updatePlaybackState({ speed: speed });
    
    if (!this.isPaused) {
      this.pausePlayback();
      this.startPlayback();
    }
  }

  toggleDropdown(): void {
    this.showDropdown = !this.showDropdown;
  }

  handleMouseDown(): void {
    this.isMouseDown = true;
    this.wasPlayingBeforeDrag = !this.isPaused;
    
    if (!this.isPaused) {
      this.pausePlayback();
    }
  }

  handleMouseUp(): void {
    this.isMouseDown = false;
    
    // Resume playback if it was playing before dragging
    if (this.wasPlayingBeforeDrag) {
      this.startPlayback();
      this.wasPlayingBeforeDrag = false;
    }
  }

  handleZoom(value: string): void {
    this.zoom = parseInt(value);
  }

  zoomIn(): void {
    if (this.zoom < 500) {
      this.zoom = Math.min(500, this.zoom + 100);
    }
  }

  zoomOut(): void {
    if (this.zoom > 100) {
      this.zoom = Math.max(100, this.zoom - 100);
    }
  }

  updateProgress(): void {
    if (this.globalTimeRange.duration === 0) {
      this.progress = 0;
      return;
    }

    this.progress = ((this.currentTime - this.globalTimeRange.min) / this.globalTimeRange.duration) * 100;
    this.currentTimeDisplay = this.formatTime(this.currentTime);
    this.mapControlService.updateCurrentTime(this.currentTime);
  }

  updateVehiclePositions(): void {
    this.vehicles.forEach(vehicle => {
      if (!vehicle.milestonesData || !vehicle.milestonesData.locationList) {
        return;
      }
      
      const location = this.findLocationAtTime(vehicle.milestonesData.locationList, this.currentTime);
      
      if (location) {
        const position: VehiclePosition = {
          vehicleId: vehicle.etsVehicle.guId,
          lat: location.lat,
          lng: location.lng,
          speed: location.speed || 0,
          heading: this.calculateHeading(vehicle.milestonesData.locationList, this.currentTime)
        };
        
        this.mapControlService.updateVehiclePosition(vehicle.etsVehicle.guId, position);
      }
    });
  }

  findLocationAtTime(locations: any[], time: number): any {
    if (!locations || locations.length === 0) return null;

    for (let i = 0; i < locations.length - 1; i++) {
      if (time >= locations[i].time && time <= locations[i + 1].time) {
        const ratio = (time - locations[i].time) / (locations[i + 1].time - locations[i].time);
        return {
          lat: locations[i].lat + (locations[i + 1].lat - locations[i].lat) * ratio,
          lng: locations[i].lng + (locations[i + 1].lng - locations[i].lng) * ratio,
          speed: locations[i].speed
        };
      }
    }

    if (time < locations[0].time) return locations[0];
    return locations[locations.length - 1];
  }

  calculateHeading(locations: any[], time: number): number {
    if (!locations || locations.length < 2) return 0;

    for (let i = 0; i < locations.length - 1; i++) {
      if (time >= locations[i].time && time <= locations[i + 1].time) {
        const lat1 = locations[i].lat * Math.PI / 180;
        const lat2 = locations[i + 1].lat * Math.PI / 180;
        const lng1 = locations[i].lng * Math.PI / 180;
        const lng2 = locations[i + 1].lng * Math.PI / 180;

        const y = Math.sin(lng2 - lng1) * Math.cos(lat2);
        const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(lng2 - lng1);
        const heading = Math.atan2(y, x) * 180 / Math.PI;

        return (heading + 360) % 360;
      }
    }

    return 0;
  }

  resetPlayer(): void {
    this.globalTimeRange = { min: 0, max: 0, duration: 0 };
    this.currentTime = 0;
    this.progress = 0;
    this.currentTimeDisplay = '00:00';
    this.tripMarkers = [];
    this.employeeMarkers = [];
    this.timestamps = [];
    this.showEmployeeMarkers = false;
  }

  ngOnDestroy(): void {
    this.pausePlayback();
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.mapControlService.clearVehiclePositions();
    this.resetPlayer();
  }

  hasReceivedVehicles: boolean = false;
}
